/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField
} from '@material-ui/core';
import { TapTapCustomerFormField } from 'src/redux/slices/taptap-customer/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  taptapGetProfilesThunk,
  taptapOptionsBranchReleased
} from 'src/redux/slices/taptap-customer';
import { getBranchColor } from 'src/utils';
import { FormikProps } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { cloneDeep } from 'lodash';
import { BranchQuestionaireFormValues } from '../types';
import {
  fields,
  initialEmployeeBuildsPerformance,
  initialPeripheralsHardsell
} from '../constants';
import { useSnackBar } from 'src/hooks';

interface Props {
  formikHook: any;
}

const { selectors: taptapCustomerSelectors } = slices.taptapCustomer;

const BranchQuestionaireForm = ({ formikHook }: Props) => {
  const formikHookRef: FormikProps<BranchQuestionaireFormValues> = formikHook;
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const profiles = useAppSelector(taptapCustomerSelectors.selectProfiles);

  const fetchTapTapProfiles = async () => {
    dispatch(taptapGetProfilesThunk());
  };

  const salesOptions = useMemo(() => {
    return profiles.map((profile) => ({
      label: profile.display_name,
      value: profile.display_name
    }));
  }, [profiles]);

  const selectedSales = useCallback(
    (salesDisplayName?: string) => {
      const value = salesOptions.find(
        (option) => option.label === salesDisplayName
      );
      return value || null; // Return the found option or null if not found
    },
    [salesOptions]
  );

  const onAddHardSellRow = () => {
    const clonedPeripheralsHardsell = cloneDeep(
      formikHookRef.values?.peripherals_hardsell
    );

    if (
      clonedPeripheralsHardsell?.length > 0 &&
      !clonedPeripheralsHardsell[clonedPeripheralsHardsell.length - 1]?.sales
    ) {
      snackBar.show({
        severity: 'error',
        message:
          'Please fill out the `SALES` last row before adding a new one.',
        useSound: true
      });
      // If the last row is empty, do not add a new row
      return;
    }

    clonedPeripheralsHardsell?.push(initialPeripheralsHardsell);
    formikHookRef.setFieldValue(
      fields.PERIPHERALS_HARDSELL,
      clonedPeripheralsHardsell
    );
  };

  const onDeleteHardSellRow = (indexToRemove: number) => {
    const clonedPeripheralsHardsell = cloneDeep(
      formikHookRef.values?.peripherals_hardsell
    );

    clonedPeripheralsHardsell.splice(indexToRemove, 1);

    formikHookRef.setFieldValue(
      fields.PERIPHERALS_HARDSELL,
      clonedPeripheralsHardsell
    );
  };

  const onAddEmployeeBuildsRow = () => {
    const clonedEmployeeBuildsPerformance = cloneDeep(
      formikHookRef.values?.employee_builds_performance
    );

    if (
      clonedEmployeeBuildsPerformance?.length > 0 &&
      !clonedEmployeeBuildsPerformance[
        clonedEmployeeBuildsPerformance.length - 1
      ]?.sales
    ) {
      snackBar.show({
        severity: 'error',
        message:
          'Please fill out the `SALES` last row before adding a new one.',
        useSound: true
      });
      // If the last row is empty, do not add a new row
      return;
    }

    clonedEmployeeBuildsPerformance?.push(initialEmployeeBuildsPerformance);
    formikHookRef.setFieldValue(
      fields.EMPLOYEE_BUILDS_PERFORMANCE,
      clonedEmployeeBuildsPerformance
    );
  };

  const onDeleteEmployeeBuildsPerformance = (indexToRemove: number) => {
    const clonedEmployeeBuildsPerformance = cloneDeep(
      formikHookRef.values?.employee_builds_performance
    );

    clonedEmployeeBuildsPerformance.splice(indexToRemove, 1);

    formikHookRef.setFieldValue(
      fields.EMPLOYEE_BUILDS_PERFORMANCE,
      clonedEmployeeBuildsPerformance
    );
  };

  useEffect(() => {
    fetchTapTapProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <form autoComplete="off">
        <Grid container spacing={2}>
          <Grid item xs={12} key="branch">
            <>
              <FormLabel id="branch">Branch</FormLabel>
              <RadioGroup
                row
                id="branch"
                name="branch"
                value={formikHookRef.values?.branch || ''}
                onChange={formikHookRef.handleChange}
              >
                {taptapOptionsBranchReleased?.map((option: any) => (
                  <FormControlLabel
                    id={option?.value}
                    key={option?.value}
                    value={option?.value}
                    control={
                      <Radio
                        onClick={() => {
                          // Check if the current option is already selected
                          if (formikHookRef.values?.branch === option.value) {
                            formikHookRef.setFieldValue('branch', ''); // Unselect by setting to an empty string
                          } else {
                            formikHookRef.setFieldValue('branch', option.value); // Select the new value
                          }
                        }}
                      />
                    }
                    label={
                      <span style={{ color: getBranchColor(option?.value) }}>
                        {option?.label}
                      </span>
                    }
                  />
                ))}
              </RadioGroup>

              {formikHookRef?.errors['branch'] && (
                <FormHelperText error>
                  {formikHookRef?.errors['branch']}
                </FormHelperText>
              )}
            </>
          </Grid>

          <Grid item xs={12} key="sales_today">
            <TextField
              id="sales_today"
              fullWidth
              label="How much is the sales today"
              name="sales_today"
              type="number"
              onChange={formikHookRef.handleChange}
              value={formikHookRef.values?.sales_today || ''}
              onBlur={formikHookRef.handleBlur}
              variant="outlined"
              error={formikHookRef.errors?.sales_today ? true : false}
              helperText={formikHookRef.errors?.sales_today || undefined}
            />
          </Grid>

          <Grid item xs={12} key="availment_powered_builds">
            <TextField
              id="availment_powered_builds"
              fullWidth
              label="Powered Builds Availed by Customers"
              name="availment_powered_builds"
              type="number"
              onChange={formikHookRef.handleChange}
              value={formikHookRef.values['availment_powered_builds'] || ''}
              onBlur={formikHookRef.handleBlur}
              variant="outlined"
              error={
                formikHookRef.errors['availment_powered_builds'] ? true : false
              }
              helperText={
                formikHookRef.errors['availment_powered_builds'] || undefined
              }
            />
          </Grid>

          <Grid item xs={12} key="availment_promo">
            <TextField
              id="availment_promo"
              fullWidth
              label="Promos Availed by Customers"
              name="availment_promo"
              type="number"
              onChange={formikHookRef.handleChange}
              value={formikHookRef.values['availment_promo'] || ''}
              onBlur={formikHookRef.handleBlur}
              variant="outlined"
              error={formikHookRef.errors['availment_promo'] ? true : false}
              helperText={formikHookRef.errors['availment_promo'] || undefined}
            />
          </Grid>

          <Grid style={{ marginTop: 10 }} item xs={12} key="item_find_today">
            <TextField
              id={'item_find_today'}
              fullWidth
              label={'Items Requested by Customers Today'}
              name={'item_find_today'}
              type="text"
              multiline
              minRows={3}
              onChange={formikHookRef.handleChange}
              value={formikHookRef.values['item_find_today'] || ''}
              onBlur={formikHookRef.handleBlur}
              variant="outlined"
              error={formikHookRef.errors['item_find_today'] ? true : false}
              helperText={formikHookRef.errors['item_find_today'] || undefined}
            />
          </Grid>

          <Grid style={{ marginTop: 10 }} item xs={12} key="pc_assembly">
            <Card>
              <CardHeader
                title="PC Assembled"
                subheader="Quantity of PC assembled in entry, mid, and high range"
              />
              <CardContent>
                <Grid container spacing={2}>
                  {[
                    'pc_assembly_entry',
                    'pc_assembly_mid',
                    'pc_assembly_high'
                  ].map((key) => (
                    <Grid item xs={12} sm={4} key={key}>
                      <TextField
                        id={key}
                        fullWidth
                        label={key
                          .replace(/_/g, ' ')
                          .replace('pc assembly ', 'PC Assembly - ')}
                        name={key}
                        type="number"
                        onChange={formikHookRef.handleChange}
                        value={formikHookRef.values[key] || ''}
                        onBlur={formikHookRef.handleBlur}
                        variant="outlined"
                        error={formikHookRef.errors[key] ? true : false}
                        helperText={formikHookRef.errors[key] || undefined}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid style={{ marginTop: 10 }} item xs={12} key="laptop_sold">
            <Card>
              <CardHeader
                title="Laptops Sold"
                subheader="Quantity of laptop sold on gaming and notebook category"
              />
              <CardContent>
                <Grid container spacing={2}>
                  {['laptop_sold_gaming', 'laptop_sold_notebook'].map(
                    (key: any) => (
                      <Grid item xs={12} sm={4} key={key}>
                        <TextField
                          id={key}
                          fullWidth
                          label={key
                            .replace(/_/g, ' ')
                            .replace('laptop sold ', 'Laptop Sold - ')}
                          name={key}
                          type="number"
                          onChange={formikHookRef.handleChange}
                          value={formikHookRef.values[key] || ''}
                          onBlur={formikHookRef.handleBlur}
                          variant="outlined"
                          error={formikHookRef.errors[key] ? true : false}
                          helperText={formikHookRef.errors[key] || undefined}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            style={{ marginTop: 10 }}
            item
            xs={12}
            key="peripherals_hardsell"
          >
            <Card>
              <CardHeader
                title="Peripherals Hard Sell"
                subheader="Quantity of peripherals sold by sales personnel"
              />
              <CardContent>
                {formikHookRef?.values?.peripherals_hardsell?.map(
                  (item, index) => (
                    <Grid key={index} container spacing={3}>
                      <Grid item xs={6}>
                        {/* <TextField
                          fullWidth
                          label="Sales"
                          size="medium"
                          value={item.sales}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          name={`peripherals_hardsell.${index}.sales`}
                          variant="outlined"
                        /> */}

                        <Autocomplete
                          id={`peripherals_hardsell.${index}.sales`}
                          options={salesOptions}
                          getOptionLabel={(option) => option?.label || ''}
                          value={selectedSales(item.sales) || null}
                          onChange={(event, value) => {
                            formikHookRef.setFieldValue(
                              `peripherals_hardsell.${index}.sales`,
                              value?.value || null
                            ); // Store only the primitive value
                          }}
                          onBlur={() => {
                            formikHookRef.setFieldTouched(
                              `peripherals_hardsell.${index}.sales`,
                              true
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Sales"
                              variant="outlined"
                              error={
                                formikHookRef.errors?.peripherals_hardsell?.[
                                  index
                                  // @ts-ignore. Ignore error for now
                                ]?.sales
                              }
                              helperText={
                                formikHookRef.errors?.peripherals_hardsell?.[
                                  index
                                  // @ts-ignore. Ignore error for now
                                ]?.sales
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Quantity"
                          size="medium"
                          value={item.quantity || ''}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          type="number"
                          name={`peripherals_hardsell.${index}.quantity`}
                          id={`peripherals_hardsell.${index}.quantity`}
                          variant="outlined"
                          error={
                            formikHookRef.errors?.peripherals_hardsell?.[
                              index
                              // @ts-ignore. Ignore error for now
                            ]?.quantity
                          }
                          helperText={
                            formikHookRef.errors?.peripherals_hardsell?.[
                              index
                              // @ts-ignore. Ignore error for now
                            ]?.quantity
                          }
                        />
                      </Grid>

                      <Grid item xs={1}>
                        <IconButton
                          aria-label="delete"
                          onClick={() => onDeleteHardSellRow(index)}
                        >
                          <DeleteOutlineIcon color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )
                )}
              </CardContent>

              <CardActions>
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={onAddHardSellRow}
                >
                  Add Row
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid
            style={{ marginTop: 10 }}
            item
            xs={12}
            key="employee_builds_performance"
          >
            <Card>
              <CardHeader
                title="Employee Builds Performance"
                subheader="Employee Builds Performance - Sales, Entry Level, Mid Level, High Level"
              />
              <CardContent>
                {formikHookRef?.values?.employee_builds_performance?.map(
                  (item, index) => (
                    <Grid key={index} container spacing={3}>
                      <Grid item xs={5}>
                        {/* <TextField
                          fullWidth
                          label="Sales"
                          size="medium"
                          value={item.sales}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          name={`employee_builds_performance.${index}.sales`}
                          variant="outlined"
                        /> */}

                        <Autocomplete
                          id={`employee_builds_performance.${index}.sales`}
                          options={salesOptions}
                          getOptionLabel={(option) => option?.label || ''}
                          value={selectedSales(item.sales) || null}
                          onChange={(event, value) => {
                            formikHookRef.setFieldValue(
                              `employee_builds_performance.${index}.sales`,
                              value?.value || ''
                            ); // Store only the primitive value
                          }}
                          onBlur={() =>
                            formikHookRef.setFieldTouched(
                              `employee_builds_performance.${index}.sales`,
                              true
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Sales"
                              variant="outlined"
                              error={
                                formikHookRef.errors
                                  ?.employee_builds_performance?.[
                                  index
                                  // @ts-ignore. Ignore error for now
                                ]?.sales
                              }
                              helperText={
                                formikHookRef.errors
                                  ?.employee_builds_performance?.[
                                  index
                                  // @ts-ignore. Ignore error for now
                                ]?.sales
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Entry Level"
                          size="medium"
                          value={item.entry_qty}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          type="number"
                          name={`employee_builds_performance.${index}.entry_qty`}
                          variant="outlined"
                          error={
                            formikHookRef.errors
                              ?.employee_builds_performance?.[
                              index
                              // @ts-ignore. Ignore error for now
                            ]?.entry_qty
                          }
                          helperText={
                            formikHookRef.errors
                              ?.employee_builds_performance?.[
                              index
                              // @ts-ignore. Ignore error for now
                            ]?.entry_qty
                          }
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Mid Level"
                          size="medium"
                          value={item.mid_qty}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          type="number"
                          name={`employee_builds_performance.${index}.mid_qty`}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="High Level"
                          size="medium"
                          value={item.high_qty}
                          onBlur={formikHookRef?.handleBlur}
                          onChange={formikHookRef?.handleChange}
                          type="number"
                          name={`employee_builds_performance.${index}.high_qty`}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            onDeleteEmployeeBuildsPerformance(index)
                          }
                        >
                          <DeleteOutlineIcon color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )
                )}
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={onAddEmployeeBuildsRow}
                >
                  Add Row
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

export default BranchQuestionaireForm;
